import { logUsage } from 'owa-analytics';
import { getGuid } from 'owa-guid';
import {
    getScenarioStore,
    getSuggestions,
    is3SServiceAvailable,
    lazyIsQFRequestIdEqualToLatest,
    lazySetLatestQFRequestId,
    lazyUpdateIsSuggestionSetComplete,
    SearchScenarioId,
    setCurrentSuggestions,
    setLatestRenderedQFTraceId,
} from 'owa-search';
import { orchestrator } from 'satcheljs';
import getSuggestionQueryString from '../selectors/getSuggestionQueryString';
import getServerSuggestions from '../utils/getSuggestions/getServerSuggestions';
import isFileSuggestionsSearchAction from '../utils/isFileSuggestionsSearchAction';
import { DEBOUNCE_DELAY_MS } from '../utils/searchConstants';

export default orchestrator(getSuggestions, async actionMessage => {
    const { scenarioId } = actionMessage;

    if (!isFileSuggestionsSearchAction(scenarioId)) {
        return;
    }

    // Get query string from the store (user inputted text + pills).
    const queryString = getSuggestionQueryString(scenarioId);

    // Log that a selection has been selected, and whether or not it was from MRU.
    const mruSelection = queryString === '';
    logUsage('InsertLinkFromSearchSuggestionSelected', {
        MRU: mruSelection,
    });

    /**
     * Generate a GUID to serve as the request ID and set it in
     * the store.
     */
    let currentQfRequestId: string | undefined = undefined;
    if (is3SServiceAvailable()) {
        currentQfRequestId = getGuid();
        const setLatestQfRequestId = await lazySetLatestQFRequestId.import()!;
        setLatestQfRequestId(currentQfRequestId, scenarioId);
    }

    const getSuggestionsInternalParameters = {
        currentQfRequestId,
        queryString,
        scenarioId,
    };

    return new Promise<void>(resolve => {
        // Wait DEBOUNCE_DELAY_MS before checking if we should get new suggestions.
        setTimeout(async () => {
            await getSuggestionsInternal(getSuggestionsInternalParameters);
            resolve();
        }, DEBOUNCE_DELAY_MS);
    });
});

async function getSuggestionsInternal({
    currentQfRequestId,
    queryString,
    scenarioId,
}: {
    queryString: string;
    scenarioId: SearchScenarioId;
    currentQfRequestId: string | undefined;
}): Promise<void> {
    const shouldGetSuggestionsResult = await shouldGetSuggestions({
        queryString: queryString,
        scenarioId: scenarioId,
        currentQfRequestId: currentQfRequestId!,
    });

    // Don't get suggestions if we shouldn't get suggestions.
    if (!shouldGetSuggestionsResult) {
        return;
    }

    // Wait for server suggestion set
    const serverSuggestionsSet = await getServerSuggestions(scenarioId, queryString);
    const suggestions = serverSuggestionsSet.Suggestions;

    const searchStore = getScenarioStore(scenarioId);
    if (
        !searchStore.currentSuggestions ||
        searchStore.currentSuggestions.TraceId !== serverSuggestionsSet.TraceId
    ) {
        // Order and filter suggestions (based on supported suggestions).
        if (suggestions.length > 0 && serverSuggestionsSet.TraceId) {
            setLatestRenderedQFTraceId(serverSuggestionsSet.TraceId, scenarioId);
        }

        setCurrentSuggestions(
            {
                Suggestions: suggestions,
                IsComplete: true,
                TraceId: serverSuggestionsSet.TraceId,
                RequestStart: serverSuggestionsSet.RequestStart,
            },
            scenarioId,
            currentQfRequestId!
        );
    }

    // Dispatch action to mark suggestion set as completed.
    if (is3SServiceAvailable()) {
        const updateIsSuggestionSetComplete = await lazyUpdateIsSuggestionSetComplete.import()!;
        updateIsSuggestionSetComplete(true /* isComplete */, scenarioId);
    }
}

/**
 * Do not fetch new suggestions if:
 * - Query text has been changed
 * - Too many requests have been issued in succession (i.e. user typing away)
 *   - The exception here is zero query. Because we clear the currentQfRequestId
 *     (because zero query isn't served by QF), it won't be equal to
 *     the latest QF request ID in the store.
 */
async function shouldGetSuggestions({
    queryString,
    currentQfRequestId,
    scenarioId,
}: {
    queryString: string;
    currentQfRequestId: string;
    scenarioId: SearchScenarioId;
}): Promise<boolean> {
    const isQFRequestIdEqualToLatest = await lazyIsQFRequestIdEqualToLatest.import()!;

    return (
        queryString === getSuggestionQueryString(scenarioId) &&
        (isQFRequestIdEqualToLatest(currentQfRequestId, scenarioId) || currentQfRequestId === null)
    );
}
